import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Bell, MonitorSmartphone, Mail, MessageSquare, CheckCheck, UserCheck, HandHeart, UserPen } from "lucide-react";

const CH_ICON = { email: Mail, sms: MessageSquare, portal: MonitorSmartphone };

export default function NotificationBell() {
  const { user } = useAuth();
  const isLeader = user?.role === "team_leader";
  const isStaffNotif = !isLeader && !!(user?.caps?.pastoral_care || user?.caps?.visitor_care || user?.caps?.evangelism);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      if (isLeader) {
        const { data } = await api.get("/leader/applications");
        setItems(data.map((a) => ({
          id: a.id, kind: "application", title: `${a.member_name} wants to join ${a.team_name}`,
          body: a.reason || "New serving application", sender: a.team_name, read: false, created_at: a.created_at,
        })));
        setCount(data.length);
      } else if (isStaffNotif) {
        const { data } = await api.get("/staff/notifications/unread-count");
        setCount(data.count);
      } else {
        const { data } = await api.get("/portal/messages/unread-count");
        setCount(data.count);
      }
    } catch {}
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, [isLeader, isStaffNotif]); // eslint-disable-line

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && isLeader) { refresh(); return; }
    if (next && isStaffNotif) {
      try { const { data } = await api.get("/staff/notifications"); setItems(data); } catch {}
      return;
    }
    if (next) {
      try { const { data } = await api.get("/portal/messages"); setItems(data); } catch {}
    }
  };

  const openItem = async (m) => {
    setOpen(false);
    if (isLeader) { navigate("/my-teams"); return; }
    if (isStaffNotif) {
      if (!m.read) { await api.post(`/staff/notifications/${m.id}/read`); setCount((c) => Math.max(0, c - 1)); }
      navigate(m.link || "/dashboard");
      return;
    }
    if (!m.read) {
      await api.post(`/portal/messages/${m.id}/read`);
      setCount((c) => Math.max(0, c - 1));
    }
    if (m.link) {
      if (/^https?:\/\//.test(m.link)) { window.open(m.link, "_blank", "noopener"); return; }
      navigate(m.link);
      return;
    }
    navigate("/portal");
  };

  const markAll = async () => {
    if (isLeader) { setOpen(false); navigate("/my-teams"); return; }
    if (isStaffNotif) {
      await api.post("/staff/notifications/read-all");
      setItems((p) => p.map((x) => ({ ...x, read: true })));
      setCount(0);
      return;
    }
    await api.post("/portal/messages/read-all");
    setItems((p) => p.map((x) => ({ ...x, read: true })));
    setCount(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={toggle}
        className="relative p-2.5 rounded-full transition-colors hover:bg-[#EEF1FA]" style={{ color: "var(--sj-text-2)" }}>
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: "var(--sj-primary)" }}>
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl border bg-white shadow-[0_12px_40px_rgba(45,45,45,0.15)] z-50 overflow-hidden" style={{ borderColor: "var(--sj-border)" }}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--sj-border)" }}>
            <span className="font-head font-semibold">{isLeader ? "Applications" : "Notifications"}</span>
            {count > 0 && (
              <button data-testid="bell-mark-all-read" onClick={markAll} className="flex items-center gap-1 text-xs font-medium" style={{ color: "var(--sj-primary)" }}>
                <CheckCheck className="h-3.5 w-3.5" /> {isLeader ? "Review all" : "Mark all read"}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm" style={{ color: "var(--sj-text-3)" }}>{isLeader ? "No pending applications." : isStaffNotif ? "No new notifications." : "No messages yet."}</div>
            )}
            {items.slice(0, 8).map((m) => {
              const Icon = m.kind === "application" ? UserCheck : m.kind === "prayer" ? HandHeart : m.kind === "profile_update" ? UserPen : (CH_ICON[m.channel] || MonitorSmartphone);
              return (
                <button key={m.id} data-testid={`bell-item-${m.id}`} onClick={() => openItem(m)}
                  className="w-full text-left flex items-start gap-3 px-4 py-3 border-b transition-colors hover:bg-[#EDF2F4]"
                  style={{ borderColor: "var(--sj-border)", background: m.read ? "#fff" : "var(--sj-peach)" }}>
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}>
                    <Icon className="h-4 w-4" style={{ color: "var(--sj-primary)" }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {!m.read && <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "var(--sj-primary)" }} />}
                      <span className="font-semibold text-sm truncate">{m.subject || m.title || "Message"}</span>
                    </div>
                    <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>from {m.sender}</div>
                    <p className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--sj-text-2)" }}>{m.body}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <button onClick={() => { setOpen(false); navigate(isLeader ? "/my-teams" : isStaffNotif ? (user?.caps?.view_prayers ? "/prayer-requests" : user?.caps?.pastoral_care ? "/pastoral-care" : "/evangelism") : "/portal"); }} className="w-full py-3 text-sm font-medium hover:bg-[#EDF2F4] transition-colors" style={{ color: "var(--sj-secondary)" }}>
            {isLeader ? "Go to my team" : isStaffNotif ? (user?.caps?.view_prayers ? "View prayer requests" : user?.caps?.pastoral_care ? "Go to Follow-Up" : "Go to Outreach") : "View all in portal"}
          </button>
        </div>
      )}
    </div>
  );
}
